import {
    DisclosurePlaceholdersResponseModel,
    MgmtApi,
    OrganizationDisclosureRequestModel,
    OrganizationDisclosureResponseModel
} from '@/customApiClients/mgmtApiClient/Api';
import { useCallback, useEffect, useMemo } from 'react';
import { atom, useRecoilState } from 'recoil';

const disclosureSettingsState =
    atom<OrganizationDisclosureResponseModel | null>({
        key: 'disclosureSettings',
        default: null
    });

const disclosureSettingsLoadingState = atom<boolean>({
    key: 'disclosureSettingsLoading',
    default: true
});

const EMPTY_PLACEHOLDERS: DisclosurePlaceholdersResponseModel = {
    solutionName: '',
    solutionPurpose: '',
    solutionDataSources: '',
    solutionUserGroups: ''
};

let fetchStarted = false;

export function useDisclosureSettings() {
    const [settings, setSettings] = useRecoilState(disclosureSettingsState);
    const [loading, setLoading] = useRecoilState(
        disclosureSettingsLoadingState
    );

    const mgmtApi = useMemo(
        () =>
            new MgmtApi({
                baseUrl: '/mgmt',
                baseApiParams: { credentials: 'include' }
            }),
        []
    );

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const res = await mgmtApi.api.v1OrganizationsDisclosureList();
            setSettings(res?.data ?? null);
        } catch {
            setSettings(null);
        } finally {
            setLoading(false);
        }
    }, [mgmtApi, setSettings, setLoading]);

    useEffect(() => {
        if (fetchStarted) return;
        fetchStarted = true;

        setLoading(true);
        mgmtApi.api
            .v1OrganizationsDisclosureList()
            .then((res) => setSettings(res?.data ?? null))
            .catch(() => {
                fetchStarted = false;
                setSettings(null);
            })
            .finally(() => setLoading(false));
    }, [mgmtApi]);

    const save = useCallback(
        async (payload: OrganizationDisclosureRequestModel) => {
            await mgmtApi.api.v1OrganizationsDisclosureCreate(payload);
            await refresh();
        },
        [mgmtApi, refresh]
    );

    return {
        settings,
        placeholders: settings?.placeholders ?? EMPTY_PLACEHOLDERS,
        enabled: settings?.enabled ?? false,
        updatedAt: settings?.updatedAt ?? null,
        loading,
        save
    };
}
